import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/providers/AuthProvider";


type UserRole = "user" | "admin" | "super_admin";

// Define the user type
interface User {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
	role: string;
	organizationName: string | null;
	organizationId: string | null;
	active: boolean;
}

const userFormSchema = z.object({
	id: z.string(),
	firstName: z.string().min(1, { message: "First name is required" }),
	lastName: z.string().min(1, { message: "Last name is required" }),
	role: z.enum(["user", "admin", "super_admin"] as const),
	organizationId: z.string().optional().nullable(),
	active: z.boolean().default(true),
});

type UserFormValues = z.infer<typeof userFormSchema>;

interface EditUserModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (data: UserFormValues) => void;
	organizations: { id: string; name: string }[];
	user: User;
}

export const EditUserModal = ({ isOpen, onClose, onSubmit, organizations, user }: EditUserModalProps) => {

	const { isSuperAdmin, isAdmin } = useAuth();
	const { t } = useTranslation();
	const form = useForm<UserFormValues>({
		resolver: zodResolver(userFormSchema),
		defaultValues: {
			id: user.id,
			firstName: user.firstName,
			lastName: user.lastName,
			role: user.role as UserRole,
			organizationId: user.organizationId,
			active: user.active,
		},
	});

	const role = form.watch("role");

	const handleSubmit = (values: UserFormValues) => {
		onSubmit(values);
		onClose(); // Close modal after successful submission
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="sm:max-w-[525px]">
				<DialogHeader>
					<DialogTitle>{t("admin.modals.editUser.title")}</DialogTitle>
					<DialogDescription>
						{t("admin.users.description")}
					</DialogDescription>
				</DialogHeader>

				<Form {...form}>
					<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 py-4">
						{/* User Information */}
						<div className="grid grid-cols-2 gap-4">
							<FormField
								control={form.control}
								name="firstName"
								render={({ field }) => (
									<FormItem>
										<FormLabel>{t("admin.user.firstName")}</FormLabel>
										<FormControl>
											<Input placeholder="John" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="lastName"
								render={({ field }) => (
									<FormItem>
										<FormLabel>{t("admin.user.lastName")}</FormLabel>
										<FormControl>
											<Input placeholder="Doe" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<FormField
							control={form.control}
							name="role"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t("admin.users.role")}</FormLabel>
									<Select
										onValueChange={field.onChange}
										defaultValue={field.value}
									>
										<FormControl>
											<SelectTrigger>
												<SelectValue placeholder={t("admin.users.selectRole")} />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											<SelectItem value="user">{t("admin.users.userRole")}</SelectItem>
											<SelectItem value="admin">{t("admin.users.adminRole")}</SelectItem>
											{isSuperAdmin && (
												<SelectItem value="super_admin">{t("admin.users.superAdminRole")}</SelectItem>
											)}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>

						{isSuperAdmin && (
							<FormField
								control={form.control}
								name="organizationId"
								render={({ field }) => (
									<FormItem>
										<FormLabel>{t("admin.users.selectCompany")}</FormLabel>
										<Select
											onValueChange={field.onChange}
											defaultValue={field.value || undefined}
										>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder={t("admin.users.selectCompanyPlaceholder")} />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{organizations.map((company) => (
													<SelectItem key={company.id} value={company.id}>
														{company.name}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>
						)}

						<FormField
							control={form.control}
							name="active"
							render={({ field }) => (
								<FormItem className="flex flex-row items-center justify-between rounded-sm border p-4 space-y-0">
									<div className="space-y-0.5">
										<FormLabel className="text-base">
											{field.value 
												? t("admin.users.active") 
												: t("admin.users.inactive")}
										</FormLabel>
										<FormDescription>
											{t("admin.users.noteAboutInactive")}
										</FormDescription>
									</div>
									<FormControl>
										<Switch
											checked={field.value}
											onCheckedChange={field.onChange}
										/>
									</FormControl>
								</FormItem>
							)}
						/>

						<div className="flex justify-end space-x-3 pt-4">
							<Button type="button" variant="outline" onClick={onClose}>
								{t("common.cancel")}
							</Button>
							<Button type="submit">{t("common.update")}</Button>
						</div>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};
